import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';

import WizardShell from './WizardShell';
import ScreensPickStep from './steps/ScreensPickStep';
import { useStartHappyHour } from '../../../queries/happyHour';

const STYLES = [
  { value: 'urgent',   emoji: '🔴', key: 'urgent' },
  { value: 'friendly', emoji: '🟢', key: 'friendly' },
  { value: 'info',     emoji: '🔵', key: 'info' },
  { value: 'premium',  emoji: '🟣', key: 'premium' },
];
const DURATIONS = [30, 60, 120, 240];

/**
 * 📢 Quick Promo — a temporary promo banner on the screens. Thin, café-worded
 * front door over the existing Happy Hour engine (POST /happy-hour/start).
 *
 * Steps: Offer (text + style) → How long → Screens.
 * Props: onExit:()=>void, onDone:()=>void
 */
export default function QuickPromoWizard({ onExit, onDone }) {
  const { t } = useTranslation();

  const [stepIndex, setStepIndex] = useState(0);
  const [text, setText] = useState('');
  const [textEn, setTextEn] = useState('');
  const [style, setStyle] = useState('urgent');
  const [minutes, setMinutes] = useState(60);
  const [deviceIds, setDeviceIds] = useState([]);

  // ⚠️ useStartHappyHour's own onSuccess already shows a success toast
  // ("Promo is live on your screens! 🔥") — this component used to show a
  // SECOND one on top of it (react-query runs the hook-level callback, then
  // the per-call one passed to .mutate()). Only onDone (navigation) belongs
  // here now.
  const start = useStartHappyHour();
  const steps = [t('cafe_wizard.step_offer'), t('cafe_wizard.step_duration'), t('cafe_wizard.step_screens')];
  const isLast = stepIndex === steps.length - 1;

  const canNext = useMemo(() => {
    if (stepIndex === 0) return text.trim().length > 0;
    if (stepIndex === 1) return minutes > 0;
    return deviceIds.length > 0;
  }, [stepIndex, text, minutes, deviceIds]);

  const submit = () => {
    start.mutate(
      {
        promo_text: text.trim(),
        promo_text_en: textEn.trim() || null,
        style,
        duration_minutes: minutes,
        device_ids: deviceIds,
      },
      { onSuccess: () => onDone?.() },
    );
  };

  const onBack = () => (stepIndex === 0 ? onExit?.() : setStepIndex((i) => i - 1));
  const onNext = () => (isLast ? submit() : setStepIndex((i) => i + 1));

  return (
    <WizardShell
      icon="📢" title={t('cafe_wizard.promo.title')} subtitle={t('cafe_wizard.promo.subtitle')}
      steps={steps} stepIndex={stepIndex}
      canNext={canNext} submitting={start.isLoading}
      nextLabel={t('cafe_wizard.show_it')}
      onBack={onBack} onNext={onNext}
    >
      {stepIndex === 0 && (
        <div className="d-flex flex-column" style={{ gap: '1rem' }}>
          <div className="pp-field">
            <label className="pp-field-label"><i className="pi pi-megaphone" /> {t('cafe_wizard.offer_text')}<span className="pp-field-required">*</span></label>
            <InputTextarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                           dir="auto" maxLength={500} className="w-100"
                           placeholder={t('cafe_wizard.offer_placeholder')} />
          </div>
          <div className="pp-field">
            <label className="pp-field-label"><i className="pi pi-language" /> {t('cafe_wizard.offer_text_en')}</label>
            <InputText value={textEn} onChange={(e) => setTextEn(e.target.value)}
                       dir="ltr" maxLength={500} className="w-100"
                       placeholder={t('cafe_wizard.offer_placeholder_en')} />
          </div>
          <div className="pp-field">
            <label className="pp-field-label"><i className="pi pi-palette" /> {t('cafe_wizard.style')}</label>
            <div className="d-flex flex-wrap gap-2">
              {STYLES.map((s) => (
                <button key={s.value} type="button" onClick={() => setStyle(s.value)}
                        className={`pp-chip ${style === s.value ? 'pp-chip-active' : ''}`} style={{ minHeight: 40 }}>
                  <span className="me-1">{s.emoji}</span>{t(`cafe_wizard.style_${s.key}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {stepIndex === 1 && (
        <div className="pp-field">
          <label className="pp-field-label"><i className="pi pi-clock" /> {t('cafe_wizard.how_long')}</label>
          <div className="d-flex flex-wrap gap-2">
            {DURATIONS.map((m) => (
              <button key={m} type="button" onClick={() => setMinutes(m)}
                      className={`pp-chip ${minutes === m ? 'pp-chip-active' : ''}`} style={{ minHeight: 40 }}>
                {t('cafe_wizard.minutes', { n: m })}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLast && <ScreensPickStep value={deviceIds} onChange={setDeviceIds} />}
    </WizardShell>
  );
}
